import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import type { Alert, AlertPreferences, AlertCategory } from '@financial-portfolio/shared/types';
import { DEFAULT_ALERT_PREFERENCES, FIRESTORE_COLLECTIONS } from '@financial-portfolio/shared/types';
import { auth, db } from '../services/firebase';

interface AlertsContextType {
  alerts: Alert[];
  unreadCount: number;
  totalAlertsCount: number; // Includes dismissed alerts - used to check if any alerts have ever been created
  preferences: AlertPreferences;
  addAlert: (alert: Omit<Alert, 'id' | 'timestamp' | 'isRead' | 'isDismissed'>) => void;
  markAsRead: (alertId: string) => void;
  markAllAsRead: () => void;
  dismissAlert: (alertId: string) => void;
  clearAlerts: () => void;
  updatePreferences: (preferences: Partial<AlertPreferences>) => void;
  getAlertsByCategory: (category: AlertCategory) => Alert[];
}

const AlertsContext = createContext<AlertsContextType | undefined>(undefined);

const ALERTS_STORAGE_KEY = 'portfolio_alerts';

export const AlertsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [preferences, setPreferences] = useState<AlertPreferences>(DEFAULT_ALERT_PREFERENCES);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Load alerts from localStorage and preferences from Firestore on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load alerts from localStorage
        const storedAlerts = localStorage.getItem(ALERTS_STORAGE_KEY);
        if (storedAlerts) {
          const parsed = JSON.parse(storedAlerts);
          const alertsWithDates = parsed.map((alert: any) => ({
            ...alert,
            timestamp: new Date(alert.timestamp),
          }));
          setAlerts(alertsWithDates);
        }

        // Load preferences from Firestore
        const user = auth.currentUser;
        if (user?.email) {
          const userDocRef = doc(db, FIRESTORE_COLLECTIONS.AUTHORIZED_USERS, user.email.toLowerCase());
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists() && userDoc.data()?.["alertPreferences"]) {
            setPreferences(userDoc.data()["alertPreferences"]);
          }
        }
      } catch (error) {
        console.error('Error loading alerts from storage:', error);
      } finally {
        setPreferencesLoaded(true);
      }
    };

    loadData();
  }, []);

  // Save alerts to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(alerts));
    } catch (error) {
      console.error('Error saving alerts to storage:', error);
    }
  }, [alerts]);

  // Save preferences to Firestore whenever they change
  useEffect(() => {
    if (!preferencesLoaded) return; // Don't save on initial load

    const savePreferences = async () => {
      try {
        const user = auth.currentUser;
        if (user?.email) {
          console.log('[AlertsContext] Saving preferences for:', user.email, preferences);
          const userDocRef = doc(db, FIRESTORE_COLLECTIONS.AUTHORIZED_USERS, user.email.toLowerCase());
          await updateDoc(userDocRef, {
            alertPreferences: preferences,
            updatedAt: new Date().toISOString() // Using ISO string for consistency with AuthContext
          });
          console.log('[AlertsContext] Preferences saved successfully');
        }
      } catch (error) {
        console.error('[AlertsContext] Error saving preferences to Firestore:', error);
        if (error instanceof Error) {
          console.error('[AlertsContext] Error details:', error.message);
        }
      }
    };

    savePreferences();
  }, [preferences, preferencesLoaded]);

  const unreadCount = alerts.filter(alert => !alert.isRead && !alert.isDismissed).length;

  const addAlert = useCallback((alertData: Omit<Alert, 'id' | 'timestamp' | 'isRead' | 'isDismissed'>) => {
    // Check if alerts are enabled and category is enabled
    if (!preferences.enabled) return;

    // For event-based alerts (rent_increase, council_rates_due, etc.), always allow through
    const eventBasedCategories = ['rent_increase', 'council_rates_due', 'water_rates_due', 'loan_review', 'fire_cert_expiry', 'pool_cert_expiry', 'bond_lodgement', 'strata_agm'];
    if (!eventBasedCategories.includes(alertData.category)) {
      const categoryPrefs = preferences.categories[alertData.category as keyof typeof preferences.categories];
      if (categoryPrefs && 'enabled' in categoryPrefs && !categoryPrefs.enabled) return;
    }

    const newAlert: Alert = {
      ...alertData,
      id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      isRead: false,
      isDismissed: false,
    };

    setAlerts(prev => [newAlert, ...prev]);
  }, [preferences]);

  const markAsRead = useCallback((alertId: string) => {
    setAlerts(prev =>
      prev.map(alert =>
        alert.id === alertId ? { ...alert, isRead: true } : alert
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setAlerts(prev =>
      prev.map(alert => ({ ...alert, isRead: true }))
    );
  }, []);

  const dismissAlert = useCallback((alertId: string) => {
    setAlerts(prev =>
      prev.map(alert =>
        alert.id === alertId ? { ...alert, isDismissed: true } : alert
      )
    );
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const updatePreferences = useCallback((newPreferences: Partial<AlertPreferences>) => {
    setPreferences(prev => ({
      ...prev,
      ...newPreferences,
      categories: {
        ...prev.categories,
        ...(newPreferences.categories || {}),
      },
    }));
  }, []);

  const getAlertsByCategory = useCallback((category: AlertCategory) => {
    return alerts.filter(alert => alert.category === category && !alert.isDismissed);
  }, [alerts]);

  return (
    <AlertsContext.Provider
      value={{
        alerts: alerts.filter(alert => !alert.isDismissed),
        unreadCount,
        totalAlertsCount: alerts.length, // Include dismissed alerts in count
        preferences,
        addAlert,
        markAsRead,
        markAllAsRead,
        dismissAlert,
        clearAlerts,
        updatePreferences,
        getAlertsByCategory,
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
};

export const useAlerts = () => {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }
  return context;
};
